import {
  PaginatedRequestParams,
  PaginatedResponse,
  RtkEmptySplitApi,
} from "../../types";
import { WithApiRequestMeta } from "../../api-results";
import {
  TicketDetailsDto,
  TicketDetailsModel,
  TicketListItemDto,
  TicketListItemModel,
  TicketCommentDto,
  TicketCommentModel,
  TicketLogDto,
  TicketLogModel,
  transformTicketCommentDtoToModel,
  transformTicketDetailsDtoToModel,
  transformTicketListItemDtoToModel,
  transformTicketLogDtoToModel,
} from "./models";
import { TicketInboxFilter } from "./models/common-types/TicketInboxFilter";
import type {
  CreateTicketCommentPayload,
  CreateTicketPayload,
  GetTicketCommentsParams,
  GetTicketLogsParams,
  GetTicketsParams,
  UpdateTicketCommentPayload,
  UpdateTicketPayload,
} from "./types";
import { TicketsApiTag } from "./tags";

export const addTicketsApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [TicketsApiTag.ticket, TicketsApiTag.ticketsStats],
  });

  const ticketsApi = apiWithTags.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
      getTickets: build.query<
        PaginatedResponse<TicketListItemModel>,
        WithApiRequestMeta<PaginatedRequestParams<GetTicketsParams>>
      >({
        query: ({
          entityId,
          page,
          page_size,
          status,
          assignee,
          contact,
          excludeResolved,
          time,
          dateType,
          from,
          to,
          recent,
          sortBy,
          sortDir,
          searchTerm,
        }) => ({
          url: `/api/auth/${entityId}/tickets`,
          method: "GET",
          params: {
            status,
            assignee,
            contact,
            exclude_resolved: excludeResolved,
            time,
            date_type: dateType,
            from,
            to,
            recent,
            sort_by: sortBy,
            sort_dir: sortDir,
            search_term: searchTerm,
            page: page || 1,
            page_size: page_size || 20,
          },
        }),
        transformResponse: (
          response: PaginatedResponse<TicketListItemDto>
        ) => ({
          ...response,
          data: response.data.map(transformTicketListItemDtoToModel),
        }),
        providesTags: [{ type: TicketsApiTag.ticket, id: "LIST" }],
      }),

      getTicketById: build.query<
        TicketDetailsModel,
        WithApiRequestMeta<{ entityId: string; ticketId: string }>
      >({
        query: ({ entityId, ticketId }) => ({
          url: `/api/auth/${entityId}/tickets/${ticketId}`,
          method: "GET",
        }),
        transformResponse: (response: TicketDetailsDto) =>
          transformTicketDetailsDtoToModel(response),
        providesTags: (_result, _error, { ticketId }) => [
          { type: TicketsApiTag.ticket, id: ticketId },
        ],
      }),

      createTicket: build.mutation<
        TicketDetailsModel,
        WithApiRequestMeta<{ entityId: string; data: CreateTicketPayload }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/tickets`,
          method: "POST",
          data,
        }),
        transformResponse: (response: TicketDetailsDto) =>
          transformTicketDetailsDtoToModel(response),
        invalidatesTags: [TicketsApiTag.ticketsStats],
      }),

      updateTicket: build.mutation<
        TicketDetailsModel,
        WithApiRequestMeta<{
          entityId: string;
          ticketId: string;
          data: UpdateTicketPayload;
        }>
      >({
        query: ({ entityId, ticketId, data }) => ({
          url: `/api/auth/${entityId}/tickets/${ticketId}`,
          method: "PUT",
          data,
        }),
        transformResponse: (response: TicketDetailsDto) =>
          transformTicketDetailsDtoToModel(response),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const response = await queryFulfilled;

            const updateTicketAction = ticketsApi.util.updateQueryData(
              "getTicketById",
              { entityId: args.entityId, ticketId: args.ticketId },
              () => response.data
            );

            dispatch(updateTicketAction);
          } catch {
            // Do nothing
          }
        },
        invalidatesTags: [TicketsApiTag.ticketsStats],
      }),

      deleteTicket: build.mutation<
        void,
        WithApiRequestMeta<{ entityId: string; ticketId: string }>
      >({
        query: ({ entityId, ticketId }) => ({
          url: `/api/auth/${entityId}/tickets/${ticketId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { ticketId }) => [
          { type: TicketsApiTag.ticket, id: ticketId },
          TicketsApiTag.ticketsStats,
        ],
      }),

      getTicketsStats: build.query<
        Partial<Record<TicketInboxFilter, number>>,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/tickets/stats`,
          method: "GET",
        }),
        providesTags: [TicketsApiTag.ticketsStats],
      }),

      getTicketLogs: build.query<
        PaginatedResponse<TicketLogModel>,
        WithApiRequestMeta<PaginatedRequestParams<GetTicketLogsParams>>
      >({
        query: ({ entityId, ticketId, page, page_size }) => ({
          url: `/api/auth/${entityId}/tickets/${ticketId}/logs`,
          method: "GET",
          params: {
            page: page || 1,
            page_size: page_size || 20,
          },
        }),
        transformResponse: (response: PaginatedResponse<TicketLogDto>) => ({
          ...response,
          data: response.data.map(transformTicketLogDtoToModel),
        }),
      }),

      getTicketComments: build.query<
        PaginatedResponse<TicketCommentModel>,
        WithApiRequestMeta<PaginatedRequestParams<GetTicketCommentsParams>>
      >({
        query: ({ entityId, ticketId, page, page_size }) => ({
          url: `/api/auth/${entityId}/tickets/${ticketId}/comments`,
          method: "GET",
          params: {
            page: page || 1,
            page_size: page_size || 20,
          },
        }),
        transformResponse: (response: PaginatedResponse<TicketCommentDto>) => ({
          ...response,
          data: response.data.map(transformTicketCommentDtoToModel),
        }),
      }),

      createTicketComment: build.mutation<
        TicketCommentModel,
        {
          entityId: string;
          ticketId: string;
          data: CreateTicketCommentPayload;
        }
      >({
        query: ({ entityId, ticketId, data }) => {
          const formData = new FormData();

          formData.append("text", data.text ?? "");

          data.files?.forEach((file) => {
            formData.append("files[]", file);
          });

          return {
            url: `/api/auth/${entityId}/tickets/${ticketId}/comments`,
            method: "POST",
            data: formData,
          };
        },
        transformResponse: (response: TicketCommentDto) =>
          transformTicketCommentDtoToModel(response),
        invalidatesTags: [TicketsApiTag.ticketsStats],
      }),

      updateComment: build.mutation<
        TicketCommentModel,
        {
          entityId: string;
          ticketId: string;
          commentId: string;
          data: UpdateTicketCommentPayload;
        }
      >({
        query: ({ entityId, ticketId, commentId, data }) => {
          const formData = new FormData();

          formData.append("text", data.text ?? "");

          data.files?.forEach((file) => {
            formData.append("files[]", file);
          });

          data.removeFiles?.forEach((filePath) => {
            formData.append("remove_files[]", filePath);
          });

          return {
            url: `/api/auth/${entityId}/tickets/${ticketId}/comments/${commentId}`,
            method: "PUT",
            data: formData,
          };
        },
        transformResponse: (response: TicketCommentDto) =>
          transformTicketCommentDtoToModel(response),
      }),

      deleteComment: build.mutation<
        void,
        {
          entityId: string;
          ticketId: string;
          commentId: string;
        }
      >({
        query: ({ entityId, ticketId, commentId }) => ({
          url: `/api/auth/${entityId}/tickets/${ticketId}/comments/${commentId}`,
          method: "DELETE",
        }),
      }),
    }),
  });

  return ticketsApi;
};

export type TicketsApi = ReturnType<typeof addTicketsApi>;
